import { useState, useEffect } from "react";
import { Search, FileText, X, ArrowUp, ArrowDown, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { STATUS_LABELS } from "@/components/StatusBadge";

const statusLabel = (s) => STATUS_LABELS[s] || s;

export function FolioSortHeader({ dir = "desc", onToggle, label = "Folio", testid = "sort-folio-button" }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#465B7F] transition-colors"
      title="Ordenar por folio"
    >
      {label}
      {dir === "asc"
        ? <ArrowUp size={13} className="text-[#465B7F]" />
        : <ArrowDown size={13} className="text-[#465B7F]" />}
    </button>
  );
}

export function folioNumber(number) {
  const nums = String(number || "").match(/\d+/g);
  return nums ? parseInt(nums[nums.length - 1], 10) : -1;
}

export function sortByFolio(list, dir = "desc") {
  const s = [...list].sort((a, b) => folioNumber(a.number) - folioNumber(b.number));
  return dir === "asc" ? s : s.reverse();
}

export function matchFilters(doc, f) {
  if (!f) return true;
  const created = (doc.created_at || "").slice(0, 10);
  if (f.date_from && created && created < f.date_from) return false;
  if (f.date_to && created && created > f.date_to) return false;
  if (f.folio && !(doc.number || "").toLowerCase().includes(f.folio.toLowerCase())) return false;
  if (f.remision && !((doc.remision_numbers || []).some((n) => (n || "").toLowerCase().includes(f.remision.toLowerCase())))) return false;
  if (f.client_id && doc.client_id !== f.client_id) return false;
  if (f.organization_id && (doc.client?.organization_id || "") !== f.organization_id) return false;
  if (f.tipo_trabajo && (doc.tipo_trabajo || "") !== f.tipo_trabajo) return false;
  if (f.status) {
    const st = Array.isArray(f.status) ? f.status : (f.status ? [f.status] : []);
    if (st.length && !st.includes(doc.status || "")) return false;
  }
  if (f.tag && !((doc.tags || []).some((t) => (t || "").toLowerCase().includes(f.tag.toLowerCase())))) return false;
  return true;
}

export function FilterBar({ clients = [], reportType, onApply, jobTypes = null, statuses = null, showRemision = false, showTags = false }) {
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [folio, setFolio] = useState("");
  const [rem, setRem] = useState("");
  const [cid, setCid] = useState("");
  const [oid, setOid] = useState("");
  const [tt, setTt] = useState("");
  const [st, setSt] = useState([]);
  const [stOpen, setStOpen] = useState(false);
  const [tag, setTag] = useState("");
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    api.get("/organizations").then((r) => setOrgs(r.data || [])).catch(() => {});
  }, []);

  const sortedClients = [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }));

  const toggleStatus = (s) => setSt((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const apply = () => onApply({ date_from: df, date_to: dt, folio, remision: rem, client_id: cid, organization_id: oid, tipo_trabajo: tt, status: st, tag });
  const clear = () => { setDf(""); setDt(""); setFolio(""); setRem(""); setCid(""); setOid(""); setTt(""); setSt([]); setTag(""); onApply({}); };
  const openReport = () => {
    const p = new URLSearchParams();
    if (df) p.set("date_from", df);
    if (dt) p.set("date_to", dt);
    if (folio) p.set("folio", folio);
    if (cid) p.set("client_id", cid);
    if (tt) p.set("tipo_trabajo", tt);
    if (st.length) p.set("status", st.join(","));
    window.open(`/reporte/${reportType}?${p.toString()}`, "_blank");
  };

  return (
    <div className="sticky top-0 z-20 border border-[#E3E8EE] dark:border-[#1D3454] bg-white dark:bg-[#10203A] p-4 mb-6 flex flex-wrap items-end gap-3">
      <Field label="Desde">
        <input type="date" value={df} onChange={(e) => setDf(e.target.value)} data-testid="filter-date-from"
          className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
      </Field>
      <Field label="Hasta">
        <input type="date" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="filter-date-to"
          className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
      </Field>
      <Field label="Folio">
        <input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="Ej. REM-0003" data-testid="filter-folio"
          className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm w-36 focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
      </Field>
      {showRemision && (
        <Field label="Remisión">
          <input value={rem} onChange={(e) => setRem(e.target.value)} placeholder="Ej. R-1362" data-testid="filter-remision"
            className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm w-36 focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
        </Field>
      )}
      <Field label="Cliente">
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="filter-client"
          className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm max-w-[12rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]">
          <option value="">Todos</option>
          {sortedClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Organización">
        <select value={oid} onChange={(e) => setOid(e.target.value)} data-testid="filter-organization"
          className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm max-w-[12rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]">
          <option value="">Todas</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </Field>
      {jobTypes && (
        <Field label="Tipo de trabajo">
          <select value={tt} onChange={(e) => setTt(e.target.value)} data-testid="filter-tipo-trabajo"
            className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm max-w-[12rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]">
            <option value="">Todas</option>
            {jobTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      )}
      {statuses && statuses.length > 0 && (
        <Field label="Estatus">
          <Popover open={stOpen} onOpenChange={setStOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid="filter-status"
                className="flex items-center justify-between gap-2 bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm min-w-[10rem] max-w-[14rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]">
                <span className="truncate">{st.length === 0 ? "Todos" : st.length === 1 ? statusLabel(st[0]) : `${st.length} seleccionados`}</span>
                <ChevronDown size={14} className="shrink-0 opacity-70" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 p-1 max-h-72 overflow-auto bg-white dark:bg-[#0C1B31] border-[#E3E8EE] dark:border-[#1D3454]" data-testid="filter-status-dropdown">
              {statuses.map((s) => (
                <label key={s} data-testid={`filter-status-option-${s}`}
                  className="flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-[#E9EEF3] dark:hover:bg-[#132339]">
                  <input type="checkbox" checked={st.includes(s)} onChange={() => toggleStatus(s)}
                    className="accent-[#465B7F]" />
                  <span className="font-mono-data text-sm text-[#16202B] dark:text-[#E7EDF6] truncate">{statusLabel(s)}</span>
                </label>
              ))}
              <div className="flex items-center justify-between gap-2 mt-1 pt-1 border-t border-[#E3E8EE] dark:border-[#1D3454]">
                {st.length > 0 ? (
                  <button type="button" onClick={() => setSt([])} data-testid="filter-status-clear"
                    className="px-3 py-2 font-mono-data text-xs text-[#5B6672] hover:text-[#16202B] dark:hover:text-white">
                    Limpiar
                  </button>
                ) : <span />}
                <button type="button" onClick={() => setStOpen(false)} data-testid="filter-status-done"
                  className="px-3 py-2 font-mono-data text-xs font-semibold text-[#465B7F] hover:underline">
                  Listo
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </Field>
      )}
      {showTags && (
        <Field label="Tag">
          <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Ej. lonas" data-testid="filter-tag"
            className="bg-white dark:bg-[#0C1B31] dark:text-[#E7EDF6] border border-[#E3E8EE] dark:border-[#1D3454] px-2 py-1.5 font-mono-data text-sm w-36 focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
        </Field>
      )}
      <button onClick={apply} data-testid="filter-apply"
        className="inline-flex items-center gap-1.5 bg-[#16202B] text-white px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-black transition-colors">
        <Search size={13} /> Filtrar
      </button>
      <button onClick={clear} data-testid="filter-clear"
        className="inline-flex items-center gap-1.5 text-[#5B6672] px-2 py-1.5 text-xs uppercase tracking-wider hover:text-[#16202B]">
        <X size={13} /> Limpiar
      </button>
      <button onClick={openReport} data-testid="filter-report"
        className="ml-auto inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors">
        <FileText size={13} /> Relación / Reporte
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-bold uppercase tracking-wide text-[#16202B] dark:text-[#C6D2E4]">{label}</span>
      {children}
    </label>
  );
}
